import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDeleteModal, AppModal, AppRemixIcon } from '@/layout/AppComponents';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import ReactDOMServer from 'react-dom/server';
import Translator from '../../../../components/helpers/Translator';
import colors from '../../../../../constants/colors';
import PublicationHome from '../PublicationHome';
import FolderHome from '../FolderHome';
import PublicationInfo from '../PublicationInfo';
import { axiosPath, path } from '../../../../../routing/helpers/router';
import minions from '../../../../../minions/minions';
import errorHandler from '@/layout/components/error/ErrorHandler';
import useIsMounted from '../../../../components/hooks/useIsMounted';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';

export default function AdminItemListView({ elementID }) {
  const { moduleBridgeHex, filtersHex } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const loading = useSelector((state) => state.currentPage?.options?.loading);
  const itLevel1 = useSelector(
    (state) => state.currentPage?.elementLevels?.level1?.[elementID],
  );
  const itLevel2 = useSelector(
    (state) => state.currentPage?.elementLevels?.level2?.[elementID],
  );
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showPublicationAddModal, setShowPublicationAddModal] = useState(false);
  const [showFolderAddModal, setShowFolderAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDelete = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            `api_r_user_module_documentManager_item_${
              itLevel1?.isFolder ? 'folder' : 'publication'
            }_delete`,
          ),
          {
            moduleBridgeHex,
            itemHex: itLevel1?.hex,
          },
        )
        .then(() => {
          if (isMounted.current) {
            dispatch(currentPageAction.change('options', { loading: true }));
            setShowDeleteModal(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, itLevel1],
  );

  const handleDoubleClick = useCallback(() => {
    if (!itLevel1 || loading) return;

    if (itLevel1?.isFolder) {
      navigate(
        path({
          name: 'user_module_documentManager_item_list',
          params: {
            moduleBridgeHex,
            parentItemHex: itLevel1?.hex,
            page: 1,
            filtersHex: 0,
          },
        }),
      );
    } else {
      setShowInfoModal(true);
    }
  }, [itLevel1, moduleBridgeHex]);

  const renderTags = useCallback(
    () =>
      itLevel1?.tags?.map((t) => (
        <span
          key={t?.id}
          className="badge badge-xs ml-1 pl-1 pr-1"
          style={{
            backgroundColor: colors.paletteColors[t?.color],
            color: '#fff',
          }}
        >
          {t?.name}
        </span>
      )),
    [itLevel1],
  );

  const renderPath = useCallback(() => {
    if (!itLevel1?.path) return '...';

    return (
      <>
        <AppRemixIcon icon="home" />
        {itLevel1?.path?.length > 0 && <> / {itLevel1?.path?.join(' / ')}</>}
      </>
    );
  }, [itLevel1]);

  return (
    <>
      <Container
        key={elementID}
        className="pt-1 pr-2 pb-3 pl-2"
        onDoubleClick={handleDoubleClick}
        isFiltering={filtersHex !== '0'}
      >
        <div className="col-title" title={itLevel1?.title}>
          <span>
            {itLevel1?.isFolder && <AppRemixIcon icon="folder" filled />}
            {itLevel1?.fileName && minions.files.fileIcon(itLevel1?.fileName)}{' '}
            {itLevel1?.title || '...'}
          </span>
        </div>
        <div>{renderTags()}</div>
        {filtersHex !== '0' && <div>{renderPath()}</div>}
        <div className="col-options">
          <div className="options">
            <span className="pointer pl-2 pr-2" title="Editar">
              <AppRemixIcon
                icon="edit-box"
                color={colors.gray700}
                onClick={() =>
                  itLevel1?.isFolder
                    ? setShowFolderAddModal(true)
                    : setShowPublicationAddModal(true)
                }
              />
            </span>
            <span
              className="pointer pl-2 pr-2"
              title="Eliminar"
              onClick={() => setShowDeleteModal(true)}
            >
              <AppRemixIcon icon="delete-bin-6" color={colors.danger} />
            </span>
          </div>
        </div>
      </Container>
      <AppModal
        show={showInfoModal && itLevel2}
        title={itLevel1?.title}
        size="xl"
        onClose={() => setShowInfoModal(false)}
      >
        <PublicationInfo
          elementID={elementID}
          onClose={() => setShowInfoModal(false)}
        />
      </AppModal>
      <AppModal
        show={showPublicationAddModal}
        title="Editar publicación"
        size="xl"
        onClose={() => setShowPublicationAddModal(false)}
        backdrop="static"
      >
        <PublicationHome
          elementID={elementID}
          onClose={() => setShowPublicationAddModal(false)}
        />
      </AppModal>
      <AppModal
        show={showFolderAddModal}
        title="Editar carpeta"
        onClose={() => setShowFolderAddModal(false)}
      >
        <FolderHome
          elementID={elementID}
          onClose={() => setShowFolderAddModal(false)}
        />
      </AppModal>
      <AppDeleteModal
        show={showDeleteModal}
        title="Eliminar"
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        sensitive
        sensitiveWord="Eliminar"
      />
    </>
  );
}

const Container = styled.div`
  border-bottom: solid 1px lightgrey;
  overflow: auto;
  display: grid;
  grid-template-columns: ${(props) =>
    props.isFiltering ? '1fr 1fr 1fr 300px' : '1fr 1fr 300px'};

  .col-title {
    cursor: default;
  }
  .col-options {
    display: flex;
    justify-content: end;
    .options {
      display: none;
    }
  }

  &:hover {
    .options {
      display: flex;
    }
  }
`;
